import { useEffect, useRef, useState } from "react";
import { getCustomisationValue, getCustomisationFieldData, findSubNode, getGroupData } from "../../EditablePreview";
import VisualisedNode, { IMAGE_MODES } from "../VisualisedNode";
import './subbomb.scss';

const SubbombMainView = (props) => {
    const customisationVars = props.node.customisationVars;
    const previewScale = props.previewScale;

    const mainViewNode = findSubNode(props.node, 'Main View');
    if (mainViewNode) {
        mainViewNode.forcePosition = 'center-value';
    }

    /* Explosion Style */
    const explosionStyle = getCustomisationValue(customisationVars, 'customImageOverlay.subExplosion.explosionStyle.explosionStyleChoice', props.index ?? 0, props.visualisationComponentKey ?? null, props.selectedFeatureNode) ?? 'default';
    const explosionScale = (getCustomisationValue(customisationVars, 'customImageOverlay.subExplosion.explosionStyle.scale', props.index ?? 0, props.visualisationComponentKey ?? null, props.selectedFeatureNode) ?? 100) / 100;
    const explosionStyleFieldData = getCustomisationFieldData(props.campaignData, props?.node?.featureData?._id, 'customImageOverlay.subExplosion.explosionStyle.explosionStyleChoice');

    const explosionAnimPreview = explosionStyleFieldData?.valOptions?.[explosionStyle]?.previewFile;
    
    const explosionStyleCss = {
        width: 1352 * previewScale * explosionScale,
        height: 760 * previewScale * explosionScale,
        opacity: 0.8,
    }
    const explosionNode = findSubNode(props.node, 'Explosion');
    const explosionItemsNode = findSubNode(props.node, 'Explosion Items');

    const addExplosionItems = getCustomisationValue(customisationVars, 'customImageOverlay.subExplosion.explosionStyle.addExplosionItems', props.index ?? 0, props.visualisationComponentKey ?? null, props.selectedFeatureNode) ?? false;
    const explosionItemsCount = getCustomisationValue(customisationVars, 'customImageOverlay.subExplosion.explosionStyle.explosionItemAmount', props.index ?? 0, props.visualisationComponentKey ?? null, props.selectedFeatureNode) ?? 10;
    const explosionItemsDataArray = getGroupData(customisationVars, 'customImageOverlay.explosionItems.image', -1, true);

    const [explosionItemPositions, setExplosionItemPositions] = useState([]);
    const initExplosionItems = () => {
        const explosionItemsOrder = [];
        for (let i = 0; i < explosionItemsDataArray.length; i++) {
            const frequency = explosionItemsDataArray[i].appearFrequency ?? 1;
            for (let j = 0; j < frequency; j++) {
                explosionItemsOrder.push(i);
            }
        }
        // Shuffle the explosion items order array
        for (let i = explosionItemsOrder.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [explosionItemsOrder[i], explosionItemsOrder[j]] = [explosionItemsOrder[j], explosionItemsOrder[i]];
        }
        // Work out x,y offsets of a circle of items
        const explosionItemPositions = [];
        for (let i = 0; i < explosionItemsCount; i++) {
            const angle = -0.1 + ((i / explosionItemsCount) * 2 * Math.PI) + Math.random() * 0.2; // Spread items evenly in a circle with some randomisation
            // Random radius, notice odd and even items have different base radius and randomness to create a more interesting explosion
            // This was basically tweaked until I ended up with something that looked like a reasonable static explosion in the preview
            const radius = (140 + 140 * (i % 2)) + (Math.random() * (100 * (i % 2) + 100));
            const x = Math.cos(angle) * radius;
            const y = Math.sin(angle) * radius;
            explosionItemPositions.push({x, y, scale: (radius / 4) / 120, radius, item: explosionItemsDataArray[explosionItemsOrder[i % explosionItemsOrder.length]], itemIndex: explosionItemsOrder[i % explosionItemsOrder.length], rotation: Math.random() * 360});
        }
        setExplosionItemPositions(explosionItemPositions);
        // console.log("Explosion item positions:", explosionItemPositions);
    }
    useEffect(() => {
        if (addExplosionItems) {
            initExplosionItems();
        }
    }, [addExplosionItems, explosionStyle, explosionItemsCount, explosionItemsDataArray.length]);

    /* Header */
    const headerType = getCustomisationValue(customisationVars, 'copy.headerSettings.settings.displayType', props.index ?? 0, props.visualisationComponentKey ?? null, props.selectedFeatureNode) ?? 'none';
    const imageHeaderScale = (headerType === 'image' ? (getCustomisationValue(customisationVars, 'copy.headerSettings.image.scale', props.index ?? 0, props.visualisationComponentKey ?? null, props.selectedFeatureNode) ?? 100) / 100 : 1);

    /* Sub Text */
    const subTextType = getCustomisationValue(customisationVars, 'copy.subTextSettings.settings.displayType', props.index ?? 0, props.visualisationComponentKey ?? null, props.selectedFeatureNode) ?? 'none';
    const imageSubTextScale = (getCustomisationValue(customisationVars, 'copy.subTextSettings.image.scale', props.index ?? 0, props.visualisationComponentKey ?? null, props.selectedFeatureNode) ?? 100) / 100;
    const subTextImageNode = findSubNode(props.node, 'Sub Text Image');

    /* Sub Count */
    const subCountNode = findSubNode(props.node, 'Counter');
    const countupSpeed = getCustomisationValue(customisationVars, 'copy.counterCopy.timing.countSpeed', props.index ?? 0, props.visualisationComponentKey ?? null, props.selectedFeatureNode) ?? 1000;
    const SubCounter = (props) => {
        const [counterValue, setCounterValue] = useState(1);
        const timeOutRef = useRef(null);

        const subCountUpDelayMult = useRef((2 - (props.countSpeed / 200 * 2) + 0.1));
        const subCountUpDelay = useRef(200);
        const subCountStep = useRef(1);

        useEffect(() => {
            if (props.timelinePlayheadLabel !== "Count Complete" && props.timelinePlayheadLabel !== "End") {
                clearTimeout(timeOutRef.current);
                const countUpFunc = () => {
                    setCounterValue((val) => {
                        if (val < 200) {
                            let newSubCountUpDelay = subCountUpDelay.current - 7;
                            if (newSubCountUpDelay < 50) newSubCountUpDelay = 50;

                            let newSubCountStep = subCountStep.current + 0.08;
                            if (newSubCountStep > 12) {
                                newSubCountStep = 12;
                            }

                            subCountUpDelay.current = newSubCountUpDelay;
                            subCountStep.current = newSubCountStep;

                            return val + Math.floor(subCountStep.current) + (newSubCountStep >= 12 ? Math.floor(Math.random() * 3) : 0);
                        }
                        subCountUpDelayMult.current = (2 - (props.countSpeed / 200 * 2) + 0.1);
                        subCountUpDelay.current = 200;
                        subCountStep.current = 1;
                        initExplosionItems();
                        return 1;
                    });
                    timeOutRef.current = setTimeout(() => {
                        countUpFunc();
                    }, Math.ceil(subCountUpDelay.current * subCountUpDelayMult.current));
                }
                timeOutRef.current = setTimeout(() => {
                    countUpFunc();
                }, Math.ceil(subCountUpDelay.current * subCountUpDelayMult.current));
            }
            return () => clearTimeout(timeOutRef.current);
        }, [props.timelinePlayheadLabel, props.countSpeed]);

        let countTo = 99;
        if (props.node?.topNode?.useParamValue) {
            if (Number(props.node.topNode.useParamValue) > 0) {
                countTo = Number(props.node.topNode.useParamValue);
            }
        }
        if (props.commandParams && props.commandParams[props.node?.featureData?._id]) {
            const paramValue = props.commandParams[props.node?.featureData?._id];
            if (Number(paramValue) >= 0) {
                countTo = Number(paramValue);
            }
            // console.log(`Using countTo ${countTo} for visualising based on commandParam value`);
        }

        return (
            <>
                +{(props.timelinePlayheadLabel === "Count Complete" || props.timelinePlayheadLabel === "End") ? countTo : counterValue <= countTo ? counterValue : countTo}
            </>
        )
    }

    return (
        <>
            {explosionAnimPreview && explosionNode && props.timelinePlayheadLabel === "Start" &&
                <VisualisedNode {...props} node={explosionNode} forcePosition="center-value">
                    <img style={explosionStyleCss} src={`/customise-previews/${explosionAnimPreview}`} alt="Explosion Preview" />
                </VisualisedNode>
            }
            {explosionStyle !== 0 && addExplosionItems && explosionAnimPreview && explosionNode && explosionItemsNode && props.timelinePlayheadLabel === "Start" &&
                <>
                    {/* Explosion Items */}
                    {explosionItemPositions.map((pos, index) => (
                        <VisualisedNode key={index} {...props} node={explosionItemsNode?.subNodes?.[pos.itemIndex] ?? explosionItemsNode} dataIndex={pos.itemIndex} forcePosition="center-value" initialXOffset={pos.x} initialYOffset={pos.y} applyScale={pos.scale * pos.item.scale / 100} applyRotation={pos.rotation} imageMode={IMAGE_MODES.scaled}>
                            {/* <img style={{width: 100 * previewScale, height: 100 * previewScale}} dataIndex={index} src={explosionItemsDataArray[explosionItemsOrder[index]].filename.srcUrl} alt={`Explosion Item ${index}`} /> */}
                        </VisualisedNode>
                    ))}
                </>
            }
            <span style={{"--v2-subanim-minscale": 1 * imageHeaderScale, "--v2-subanim-maxscale": 1.2 * imageHeaderScale}}>
                {headerType === 'image' &&
                    <VisualisedNode {...props} node={findSubNode(props.node, 'Header Image')} forcePosition="center-value" initialYOffset={-100} className="sub-incoming-anim" style={{padding: 10}} />
                }
                {headerType === 'text' &&
                    <VisualisedNode {...props} node={findSubNode(props.node, 'Header Text')} forcePosition="center-value" style={{whiteSpace: 'nowrap', padding: 10}} initialYOffset={-100} className="sub-incoming-anim" />
                }
            </span>
            
            <VisualisedNode {...props} node={subCountNode} forcePosition="center-value" style={{padding: "0px 30px"}}>
                <SubCounter {...props} countSpeed={countupSpeed} />
            </VisualisedNode>
            
            {subTextType === 'image' &&
                <VisualisedNode {...props} node={subTextImageNode} forcePosition="center-value" initialYOffset={90} imageMode={IMAGE_MODES.scaled} applyScale={imageSubTextScale} applyScaleToPosition={false} />
            }
            {subTextType === 'text' &&
                <VisualisedNode {...props} node={findSubNode(props.node, 'Sub Text Text')} forcePosition="center-value" style={{whiteSpace: 'nowrap', padding: 10}} initialYOffset={90} />
            }
        </>
    )
}

export default SubbombMainView;
